import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Clock, CheckCircle, Truck, Play, Inbox, ChevronRight } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const fadeUp = { hidden: { opacity: 0, y: 24 }, visible: (i) => ({ opacity: 1, y: 0, transition: { delay: i * 0.05, duration: 0.5 } }) };

const STAGES = [
  { key: "inquiry", label: "Inquiry", icon: Inbox, color: "text-white/60 bg-white/5 border-white/10" },
  { key: "confirmed", label: "Confirmed", icon: CheckCircle, color: "text-blue-400 bg-blue-400/5 border-blue-400/20" },
  { key: "in_progress", label: "In Progress", icon: Play, color: "text-gold bg-gold/5 border-gold/20" },
  { key: "completed", label: "Completed", icon: Clock, color: "text-green-400 bg-green-400/5 border-green-400/20" },
  { key: "delivered", label: "Delivered", icon: Truck, color: "text-purple-400 bg-purple-400/5 border-purple-400/20" },
];

export default function PipelineDashboard() {
  const [pipeline, setPipeline] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadPipeline(); }, []);

  const loadPipeline = async () => {
    try {
      const res = await api.get("/admin/pipeline");
      setPipeline(res.data || {});
    } catch { toast.error("Failed to load pipeline."); }
    finally { setLoading(false); }
  };

  const moveStage = async (bookingId, newStage) => {
    try {
      await api.patch(`/admin/pipeline/${bookingId}`, { stage: newStage });
      toast.success(`Moved to ${newStage.replace("_", " ")}`);
      loadPipeline();
    } catch { toast.error("Move failed."); }
  };

  const total = Object.values(pipeline).reduce((sum, arr) => sum + (arr?.length || 0), 0);

  if (loading) return <div className="py-20 text-center text-gold/50 font-body animate-pulse">Loading Pipeline...</div>;

  return (
    <div data-testid="pipeline-dashboard">
      <div className="flex items-center justify-between mb-6">
        <div>
          <p className="text-[10px] font-body uppercase tracking-[0.2em] text-gold/60">Operations</p>
          <h2 className="font-heading text-xl text-white">Event Pipeline</h2>
          <p className="text-white/30 text-xs font-body">{total} bookings total</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
        {STAGES.map((stage, si) => {
          const items = pipeline[stage.key] || [];
          const Icon = stage.icon;
          return (
            <motion.div key={stage.key} initial="hidden" animate="visible" variants={fadeUp} custom={si} className={`border rounded-sm overflow-hidden ${stage.color}`} data-testid={`pipeline-${stage.key}`}>
              <div className="p-3 border-b border-inherit flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Icon size={14} />
                  <span className="text-xs font-body font-medium">{stage.label}</span>
                </div>
                <span className="text-[10px] font-body opacity-60">{items.length}</span>
              </div>
              <div className="p-2 space-y-2 min-h-[120px] max-h-[400px] overflow-y-auto">
                {items.length === 0 && <p className="text-white/15 text-[10px] font-body text-center py-4">No bookings</p>}
                {items.map(b => (
                  <div key={b.id} className="bg-black/20 border border-white/5 p-3 group" data-testid={`booking-${b.id}`}>
                    <p className="text-white text-xs font-body font-medium truncate">{b.name || b.client_name || "Client"}</p>
                    <p className="text-white/30 text-[10px] font-body truncate">{b.event_type || "Event"} — {b.email || ""}</p>
                    <p className="text-white/20 text-[9px] font-body mt-1">{b.event_date || b.date || ""}</p>
                    {si < STAGES.length - 1 && (
                      <button onClick={() => moveStage(b.id, STAGES[si + 1].key)} className="mt-2 flex items-center gap-1 text-[9px] font-body text-gold/50 hover:text-gold transition-colors" data-testid={`move-${b.id}`}>
                        Move to {STAGES[si + 1].label} <ChevronRight size={10} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}
